import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Admin sign-in page. Paid members never see this — they arrive via the Squad app
 * with URL handoff params (?email=&memberId=&name=).
 */
export default function Login() {
    const { login } = useAdminAuth();
    const navigate = useNavigate();

    const [form, setForm] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const result = await login(form.email.trim(), form.password);
        setLoading(false);
        if (result.ok) {
            toast.success("Welcome, curator.");
            navigate("/admin");
        } else {
            setError(result.error);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-16 page-fade-in" data-testid="login-page">
            <div className="w-full max-w-md">
                <div className="text-center mb-12">
                    <Link to="/login" className="inline-flex items-center gap-3 group">
                        <span className="w-2 h-2 rounded-full bg-[#cfa052] shadow-[0_0_12px_rgba(207,160,82,0.6)]" />
                        <span className="font-serif-display text-2xl tracking-wide text-[#e8e4db]">
                            Angel <span className="italic">Squad</span>
                        </span>
                    </Link>
                    <h1 className="mt-10 font-serif-display text-4xl md:text-5xl tracking-tighter text-[#e8e4db]" data-testid="login-title">
                        Curator sign-in
                    </h1>
                    <p className="mt-3 font-poetic text-[#9a958a]">
                        For administrators of the squad.
                    </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-7" data-testid="login-form">
                    <div>
                        <label className="font-label text-[10px] text-[#cfa052]">Email</label>
                        <input
                            required
                            type="email"
                            value={form.email}
                            onChange={onChange("email")}
                            className="mt-2 w-full bg-transparent border-b border-white/20 text-[#e8e4db] placeholder:text-[#6e6a62] focus:border-[#cfa052] focus:outline-none py-3 transition-colors"
                            placeholder="admin@angelsquad.app"
                            data-testid="login-email-input"
                        />
                    </div>
                    <div>
                        <label className="font-label text-[10px] text-[#cfa052]">Password</label>
                        <input
                            required
                            type="password"
                            minLength={6}
                            value={form.password}
                            onChange={onChange("password")}
                            className="mt-2 w-full bg-transparent border-b border-white/20 text-[#e8e4db] placeholder:text-[#6e6a62] focus:border-[#cfa052] focus:outline-none py-3 transition-colors"
                            placeholder="••••••••"
                            data-testid="login-password-input"
                        />
                    </div>

                    {error && (
                        <div className="font-poetic text-sm text-[#d97c6a]" data-testid="login-error">{error}</div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit-btn"
                        className="w-full bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] font-medium tracking-widest uppercase text-xs py-4 rounded-sm transition-colors duration-300 disabled:opacity-60"
                    >
                        {loading ? "…" : "Enter"}
                    </button>

                    <p className="text-center font-poetic text-sm text-[#6e6a62]" data-testid="login-member-hint">
                        Are you a paid member? Open the Squad app and tap <span className="text-[#cfa052]">My Angels</span>.
                    </p>
                </form>
            </div>
        </div>
    );
}
